import { Check, ListChecks, X } from "lucide-react";
import { AlbumSummary } from "@/components/ai/AlbumSummary";
import { CheckWithAiButton } from "@/components/ai/CheckWithAiButton";
import { ChecklistItemProgress } from "@/components/checklist/ChecklistItemProgress";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useConfirmSuggestion, useRejectSuggestion } from "@/hooks/use-ai-review";
import { useChecklist, useSetCheck } from "@/hooks/use-checklist";
import { formatDate } from "@/lib/format";
import { checkSessionAfterImageError, photoFileUrl } from "@/lib/photo-files";
import type { ChecklistAlbum, ChecklistItem } from "@/schemas/checklist";
import type { ProjectDetail } from "@/schemas/photos";

function CheckedNote({ item }: { item: ChecklistItem }) {
  if (!item.checked) return null;
  const note =
    item.checkedBy && item.checkedAt
      ? `Checked by ${item.checkedBy} on ${formatDate(item.checkedAt)}`
      : null;
  if (!note) return null;
  return (
    <span data-testid="checked-note" className="text-xs text-muted-foreground">
      {note}
    </span>
  );
}

type Suggestion = NonNullable<ChecklistItem["suggestions"]>[number];

/** AI matches for an item, waiting for a PM. The AI never checks or links anything itself. */
function Suggestions({
  suggestions,
  onOpenPhoto,
}: {
  suggestions: Suggestion[];
  onOpenPhoto: (photoId: string) => void;
}) {
  const confirm = useConfirmSuggestion();
  const reject = useRejectSuggestion();
  const busy = confirm.isPending || reject.isPending;
  return (
    <div data-testid="item-suggestions" className="space-y-2">
      <p className="text-xs text-muted-foreground">Suggested, needs confirming</p>
      <ul className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
        {suggestions.map((suggestion) => (
          <li
            key={suggestion.id}
            data-testid="item-suggestion"
            className="flex min-w-0 flex-col gap-1.5 sm:w-48"
          >
            <Button
              variant="outline"
              aria-label={`Open ${suggestion.fileName}`}
              onClick={() => onOpenPhoto(suggestion.photoId)}
              className="h-24 w-full overflow-hidden p-0"
            >
              <img
                src={photoFileUrl(suggestion.photoId, "thumb")}
                alt=""
                loading="lazy"
                onError={checkSessionAfterImageError}
                className="size-full object-cover"
              />
            </Button>
            <span className="truncate text-xs text-muted-foreground" title={suggestion.fileName}>
              {suggestion.fileName}
            </span>
            <div className="flex flex-col gap-1.5 sm:flex-row">
              <Button
                size="sm"
                className="flex-1 px-2"
                aria-label={`Confirm ${suggestion.fileName}`}
                data-testid="confirm-suggestion"
                disabled={busy}
                onClick={() => confirm.mutate(suggestion.id)}
              >
                <Check className="size-4" />
                Confirm
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 px-2"
                aria-label={`Reject ${suggestion.fileName}`}
                data-testid="reject-suggestion"
                disabled={busy}
                onClick={() => reject.mutate(suggestion.id)}
              >
                <X className="size-4" />
                Reject
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ItemRow({
  item,
  canEdit,
  onOpenPhotos,
}: {
  item: ChecklistItem;
  canEdit: boolean;
  onOpenPhotos: (photoId: string) => void;
}) {
  const setCheck = useSetCheck();
  return (
    <li
      data-testid="checklist-item"
      data-checked={item.checked}
      data-missing={item.missing ?? undefined}
      className="space-y-2 py-4"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 font-medium">
            {item.name}
            {item.missing && (
              <Badge variant="warning" data-testid="item-missing">
                Missing
              </Badge>
            )}
          </div>
          {item.guidance !== item.name && (
            <p className="text-sm text-muted-foreground">{item.guidance}</p>
          )}
        </div>
        <ChecklistItemProgress
          requiredCount={item.requiredCount}
          assignedCount={item.assignedCount}
        />
        {canEdit ? (
          <Button
            variant={item.checked ? "default" : "outline"}
            size="sm"
            aria-pressed={item.checked}
            data-testid="check-item"
            disabled={setCheck.isPending}
            onClick={() =>
              setCheck.mutate({ itemId: item.id, request: { checked: !item.checked } })
            }
          >
            <Check className="size-4" />
            {item.checked ? "Checked" : "Check off"}
          </Button>
        ) : (
          <Badge variant={item.checked ? "default" : "outline"} data-testid="item-status">
            {item.checked ? "Checked" : "Not checked yet"}
          </Badge>
        )}
      </div>
      <CheckedNote item={item} />
      {item.photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {item.photos.map((photo) => (
            <Button
              key={photo.id}
              variant="outline"
              size="sm"
              data-testid="item-photo"
              onClick={() => onOpenPhotos(photo.id)}
            >
              {photo.fileName}
            </Button>
          ))}
        </div>
      )}
      {item.suggestions && item.suggestions.length > 0 && (
        <Suggestions suggestions={item.suggestions} onOpenPhoto={onOpenPhotos} />
      )}
    </li>
  );
}

/** A PM's view of the AI on an album: its summary, and the items with no photo at all. */
function AlbumAi({ album }: { album: ChecklistAlbum }) {
  const missing = album.items.filter((item) => item.missing);
  if (!album.summary && missing.length === 0) return null;
  return (
    <div className="space-y-3 border-b px-4 py-3">
      <AlbumSummary summary={album.summary} />
      {missing.length > 0 && (
        <div data-testid="missing-list">
          <h4 className="text-xs text-muted-foreground">Missing</h4>
          <p className="mt-0.5 text-sm">{missing.map((item) => item.name).join(", ")}</p>
        </div>
      )}
    </div>
  );
}

/** The MC checklist of each album that has one. A PM checks items; an IE sees status. */
export function ChecklistTab({
  project,
  canEdit,
  onOpenPhoto,
}: {
  project: ProjectDetail;
  canEdit: boolean;
  /** Open a photo in the viewer, in the album it sits in. */
  onOpenPhoto: (albumId: string, photoId: string) => void;
}) {
  const { data, isPending, isError, refetch } = useChecklist(project.id);

  if (isPending) return <Skeleton className="h-40 w-full" />;
  if (isError)
    return <ErrorState message="Could not load the checklist" onRetry={() => refetch()} />;
  if (data.albums.length === 0) {
    return (
      <EmptyState
        icon={ListChecks}
        title="No checklist on this project"
        className="rounded-lg border border-dashed border-border py-16"
      />
    );
  }
  return (
    <div className="space-y-6" data-testid="checklist-tab">
      <p className="text-sm text-muted-foreground">
        Items are MESH 1's own album help text; the one photo each needs is our default, to be
        confirmed with Madison.{" "}
        {canEdit
          ? "It is a guide: you can check an item whatever the count, and nothing is held up by it."
          : "It shows what has been shared with your firm."}
      </p>
      {data.albums.map((album) => (
        <section key={album.albumId} className="rounded-lg border" data-testid="checklist-album">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
            <h3 className="font-medium">{album.albumName}</h3>
            {canEdit && <CheckWithAiButton projectId={project.id} albumId={album.albumId} />}
          </div>
          {canEdit && <AlbumAi album={album} />}
          <ul className="divide-y px-4">
            {album.items.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                canEdit={canEdit}
                onOpenPhotos={(photoId) => onOpenPhoto(album.albumId, photoId)}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
